import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { MenuCategoryTabs } from '../components/restaurant/MenuCategoryTabs';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { FloatingCartButton } from '../components/restaurant/FloatingCartButton';
import { useRestaurantBySlug, useMenuData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, ArrowLeft } from 'lucide-react';
import { MenuItem, CartItemOption } from '../lib/types';
export function MenuPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    loading: restaurantLoading
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    addItem
  } = useCart();
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Set initial active category
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 140; // Offset for sticky headers
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options
    }, restaurant.name);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    }
  };
  if (restaurantLoading || menuLoading) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1183">
        <MarketplaceHeader data-id="element-1184" />
        <div className="max-w-5xl mx-auto px-4 w-full mt-8" data-id="element-1185">
          <Skeleton className="h-12 w-1/3 mb-8" data-id="element-1186" />
          <div className="flex gap-4 mb-8" data-id="element-1187">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 w-24 rounded-full" data-id="element-1188" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1189">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-32 rounded-2xl" data-id="element-1190" />)}
          </div>
        </div>
      </div>;
  }
  if (!restaurant) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1191">
        <MarketplaceHeader data-id="element-1192" />
        <div className="flex-1 flex items-center justify-center" data-id="element-1193">
          <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-1194" />
        </div>
      </div>;
  }
  const primaryColor = restaurant.primary_color || '#F59E0B';
  return <div className="min-h-screen flex flex-col bg-brand-offwhite pb-32" data-id="element-1195">
      <MarketplaceHeader data-id="element-1196" />

      {/* Mini Header */}
      <div className="bg-white border-b border-gray-100 py-4 sticky top-16 z-30" data-id="element-1197">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4" data-id="element-1198">
          <button onClick={() => navigate(`/r/${restaurant.slug}`)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-1199">
            <ArrowLeft className="w-5 h-5" data-id="element-1200" />
          </button>
          <div className="flex items-center gap-3" data-id="element-1201">
            {restaurant.logo_url && <img src={restaurant.logo_url} alt={restaurant.name} className="w-8 h-8 rounded-md object-cover" data-id="element-1202" />}
            <h1 className="text-xl font-bold text-gray-900 leading-none" data-id="element-1203">
              {restaurant.name}
            </h1>
          </div>
        </div>
      </div>

      <MenuCategoryTabs categories={categories} activeCategory={activeCategory} onSelect={handleCategorySelect} primaryColor={primaryColor} data-id="element-1204" />

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-1205">
        {categories.length === 0 ? <EmptyState icon={Store} title="Menú no disponible" description="Este restaurante aún no ha configurado su menú." data-id="element-1206" /> : <div className="space-y-12" data-id="element-1207">
            {categories.map(category => {
          const categoryItems = items.filter(item => item.category_id === category.id);
          if (categoryItems.length === 0) return null;
          return <section key={category.id} id={`category-${category.id}`} className="scroll-mt-36" data-id="element-1208">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1209">
                    {category.name}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1210">
                    {categoryItems.map(item => <MenuItemCard key={item.id} item={item} onClick={handleProductClick} primaryColor={primaryColor} data-id="element-1211" />)}
                  </div>
                </section>;
        })}
          </div>}
      </main>

      <FloatingCartButton primaryColor={primaryColor} data-id="element-1212" />

      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={primaryColor} data-id="element-1213" />
    </div>;
}